/**
 * CalibrationResultsGrid - test results of a calibration (bottom left of the
 * calibration page).
 *
 * Each row is one test age: the cylinders broken (strength, ASTM C1074 8.4:
 * two, a third when they differ by more than 10 %) and the maturity of the
 * sensor cylinders at that time (8.5). Broken cylinders and sensor cylinders
 * are different cylinders, so they are separate column groups.
 *
 * System: maturity is read from the sensors when the row is saved. Manual:
 * the average maturity is typed from the outside meter.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as SaveIcon,
  Close as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Warning as WarnIcon,
} from '@mui/icons-material';
import calibrationsService, { CalibrationSample, CalibrationSpecimen } from '../../services/calibrations';
import { apiErrorText } from '../../utils/apiError';
import { fmt1, formatAge, formatAgeDays, toLocalInputValue } from './calibrationFormat';

interface Props {
  calId: string;
  samples: CalibrationSample[];
  isSystem: boolean;
  specimens: CalibrationSpecimen[];
  readOnly: boolean;
  strengthUnit: string;
  maturityUnit: string;
  /** The database can store a third cylinder (ASTM C1074 8.4). */
  thirdCylinderAvailable?: boolean;
  /** Called after any change so the page reloads results, checks and charts. */
  onChanged: () => void;
  onError: (message: string) => void;
}

interface Draft {
  sampled_time: string;
  strength1: string;
  strength2: string;
  strength3: string;
  avg_ea: string;
}

const EMPTY: Draft = { sampled_time: '', strength1: '', strength2: '', strength3: '', avg_ea: '' };

const num = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v));

const draftFrom = (s: CalibrationSample): Draft => ({
  sampled_time: s.sampled_time ? s.sampled_time.slice(0, 16) : '',
  strength1: s.strength1 != null ? String(s.strength1) : '',
  strength2: s.strength2 != null ? String(s.strength2) : '',
  strength3: s.strength3 != null ? String(s.strength3) : '',
  avg_ea: s.avg_ea != null ? String(s.avg_ea) : '',
});

const pairRangePct = (d: Draft): number | null => {
  const a = num(d.strength1);
  const b = num(d.strength2);
  if (a == null || b == null || !(a > 0) || !(b > 0)) return null;
  return (Math.abs(a - b) / ((a + b) / 2)) * 100;
};

const CalibrationResultsGrid: React.FC<Props> = ({
  calId, samples, isSystem, specimens, readOnly, strengthUnit, maturityUnit, onChanged, onError,
  thirdCylinderAvailable = true,
}) => {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [rowError, setRowError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showThird, setShowThird] = useState(false);

  const sensorSlots = specimens.slice().sort((a, b) => a.slot - b.slot);
  const third = thirdCylinderAvailable
    && (showThird || samples.some((s) => s.strength3 != null || s.needs_third_cylinder));

  const startAdd = () => {
    setEditingId(null);
    setDraft({ ...EMPTY, sampled_time: toLocalInputValue(new Date()) });
    setRowError(null);
    setAdding(true);
  };

  const startEdit = (s: CalibrationSample) => {
    setAdding(false);
    setDraft(draftFrom(s));
    setRowError(null);
    setEditingId(s.id);
    if (s.needs_third_cylinder || s.strength3 != null) setShowThird(true);
  };

  const cancel = () => {
    setAdding(false);
    setEditingId(null);
    setRowError(null);
  };

  const validate = (): string | null => {
    if (!draft.sampled_time) return 'Enter the test date and time';
    const values = [draft.strength1, draft.strength2, draft.strength3, draft.avg_ea].map(num);
    if (values.some((v) => v != null && (!Number.isFinite(v) || v < 0))) return 'Values must be positive numbers';
    if (num(draft.strength1) == null && num(draft.strength2) == null) return 'Enter at least one cylinder strength';
    if (!isSystem && !(num(draft.avg_ea) && (num(draft.avg_ea) as number) > 0)) return 'Enter the maturity read from your meter';
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      setRowError(problem);
      return;
    }
    setBusy(true);
    setRowError(null);
    const body: Partial<CalibrationSample> = {
      sampled_time: draft.sampled_time,
      // System calibrations read the sensors at this instant (stored in UTC).
      sampled_time_utc: new Date(draft.sampled_time).toISOString(),
      strength1: num(draft.strength1),
      strength2: num(draft.strength2),
    };
    if (num(draft.strength3) != null) body.strength3 = num(draft.strength3);
    else if (editingId && samples.find((s) => s.id === editingId)?.strength3 != null) body.clear_strength3 = true;
    if (!isSystem) body.avg_ea = num(draft.avg_ea);
    try {
      if (editingId) await calibrationsService.updateSample(calId, editingId, body);
      else await calibrationsService.createSample(calId, body);
      cancel();
      onChanged();
    } catch (e) {
      // Shown on the row: for System this is the sensor reason (no readings, gap).
      setRowError(apiErrorText(e, 'The test result could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (s: CalibrationSample, field: keyof CalibrationSample, value: boolean) => {
    try {
      await calibrationsService.updateSample(calId, s.id, { [field]: value } as Partial<CalibrationSample>);
      onChanged();
    } catch (e) {
      onError(apiErrorText(e, 'The test result could not be updated'));
    }
  };

  const remove = async (s: CalibrationSample) => {
    if (!window.confirm('Delete this test result?')) return;
    try {
      await calibrationsService.deleteSample(calId, s.id);
      onChanged();
    } catch (e) {
      onError(apiErrorText(e, 'The test result could not be deleted'));
    }
  };

  const set = (field: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [field]: e.target.value }));

  const draftRange = pairRangePct(draft);

  // Specimen column groups, as in the mockup: each specimen has a strength
  // (its broken cylinder) and a maturity (its logger, System only).
  // Specimen 3 appears when a third cylinder is needed or a third logger is set.
  const specimenCount = Math.max(2, third ? 3 : 2, isSystem ? sensorSlots.length : 0);
  const slots = Array.from({ length: specimenCount }, (_, i) => i + 1);
  const sensorFor = (n: number) => sensorSlots.find((s) => s.slot === n);
  const colSpan = 2 + specimenCount * 2 + 2 + (readOnly ? 0 : 2);

  const head = { fontSize: 12, color: 'text.secondary', py: 0.5, lineHeight: 1.2, whiteSpace: 'nowrap' as const };
  const groupHead = { ...head, borderBottom: 'none', textAlign: 'center' as const, fontWeight: 500, color: 'text.primary' };
  const sub = (text: string) => <TableCell sx={head} align="right">{text}</TableCell>;

  const strengthCell = (s: CalibrationSample, n: number) => {
    const value = n <= 3 ? (s[`strength${n}` as 'strength1'] as number | null | undefined) : undefined;
    const exKey = `is_exclude_strength${n}` as 'is_exclude_strength1';
    if (value == null) {
      return (
        <TableCell align="right">
          {n === 3 && s.needs_third_cylinder ? (
            <Tooltip title={`Specimens 1 and 2 differ by ${s.pair_range_pct?.toFixed(0)} %. Test a third cylinder (ASTM C1074 8.4).`}>
              <WarnIcon fontSize="small" color="warning" />
            </Tooltip>
          ) : '-'}
        </TableCell>
      );
    }
    return (
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <span style={{ textDecoration: s[exKey] ? 'line-through' : undefined }}>{fmt1(value)}</span>
        {!readOnly && (
          <Tooltip title={s[exKey] ? 'Count this specimen' : 'Exclude this specimen'}>
            <Checkbox size="small" sx={{ p: 0.25, ml: 0.25 }} checked={!s[exKey]}
              onChange={(e) => toggle(s, exKey, !e.target.checked)} inputProps={{ 'aria-label': `count specimen ${n}` }} />
          </Tooltip>
        )}
      </TableCell>
    );
  };

  const maturityCell = (s: CalibrationSample, n: number) => {
    const sensor = sensorFor(n);
    const v = isSystem && sensor ? (s[`channel${n}_ea` as 'channel1_ea'] as number | undefined) : undefined;
    return (
      <TableCell align="right" sx={{ color: sensor && !sensor.accepted ? 'text.disabled' : undefined }}>{fmt1(v)}</TableCell>
    );
  };

  const numberField = (field: keyof Draft, label: string) => (
    <TextField size="small" type="number" value={draft[field]} onChange={set(field)}
      inputProps={{ 'aria-label': label, min: 0, step: 0.1, style: { padding: '4px 6px', width: 60, textAlign: 'right' } }} />
  );

  const editRow = (key: string) => (
    <React.Fragment key={key}>
      <TableRow sx={{ bgcolor: 'action.hover' }}>
        <TableCell colSpan={2}>
          <TextField size="small" type="datetime-local" value={draft.sampled_time} onChange={set('sampled_time')}
            inputProps={{ 'aria-label': 'test date and time', style: { padding: '4px 6px' } }} />
        </TableCell>
        {slots.map((n) => (
          <React.Fragment key={n}>
            <TableCell align="right">
              {n <= 3 ? numberField(`strength${n}` as keyof Draft, `specimen ${n} strength`) : '-'}
            </TableCell>
            <TableCell align="right">
              <Typography variant="caption" color="text.secondary">{isSystem && sensorFor(n) ? 'from logger' : '-'}</Typography>
            </TableCell>
          </React.Fragment>
        ))}
        <TableCell />
        <TableCell align="right">{isSystem ? null : numberField('avg_ea', 'average maturity')}</TableCell>
        <TableCell colSpan={readOnly ? 0 : 2} align="right" sx={{ whiteSpace: 'nowrap' }}>
          <IconButton size="small" color="primary" onClick={save} disabled={busy} aria-label="save test result">
            {busy ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={cancel} aria-label="cancel"><CancelIcon fontSize="small" /></IconButton>
        </TableCell>
      </TableRow>
      {(rowError || (draftRange != null && draftRange > 10 && num(draft.strength3) == null)) && (
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ py: 0.5 }}>
            {rowError ? (
              <Alert severity="error" sx={{ py: 0 }}>{rowError}</Alert>
            ) : (
              <Alert severity="warning" sx={{ py: 0 }}
                action={!third && thirdCylinderAvailable
                  ? <Button size="small" onClick={() => setShowThird(true)}>Add specimen 3</Button> : undefined}>
                Specimens 1 and 2 differ by {draftRange?.toFixed(0)} %. ASTM C1074 8.4: test a third cylinder.
              </Alert>
            )}
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );

  return (
    <Box>
      <TableContainer>
        <Table size="small" aria-label="test results">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...head, borderBottom: 'none' }} />
              <TableCell sx={{ ...head, borderBottom: 'none' }} />
              {slots.map((n) => (
                <TableCell key={n} colSpan={2} sx={groupHead}>
                  {isSystem && sensorFor(n) ? (
                    <Tooltip title={`${sensorFor(n)?.logger_code || ''} ch ${sensorFor(n)?.channel_no ?? ''}, ${sensorFor(n)?.name}`}>
                      <span>Specimen {n}</span>
                    </Tooltip>
                  ) : <>Specimen {n}</>}
                </TableCell>
              ))}
              <TableCell colSpan={2} sx={groupHead}>Average</TableCell>
              {!readOnly && <TableCell colSpan={2} sx={{ ...head, borderBottom: 'none' }} />}
            </TableRow>
            <TableRow>
              <TableCell sx={head}>Date and time</TableCell>
              <TableCell sx={head}>Elapsed</TableCell>
              {slots.map((n) => (
                <React.Fragment key={n}>
                  {sub(strengthUnit || 'Strength')}
                  {sub(maturityUnit)}
                </React.Fragment>
              ))}
              {sub(strengthUnit || 'Strength')}
              {sub(maturityUnit)}
              {!readOnly && <TableCell sx={head} align="center">Use</TableCell>}
              {!readOnly && <TableCell sx={head} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {samples.map((s) =>
              editingId === s.id ? editRow(s.id) : (
                <TableRow key={s.id} sx={{ opacity: s.is_exclude ? 0.5 : 1 }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {s.sampled_time ? new Date(s.sampled_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatAgeDays(s.age_hours)}><span>{formatAge(s.age_hours)}</span></Tooltip>
                  </TableCell>
                  {slots.map((n) => (
                    <React.Fragment key={n}>
                      {strengthCell(s, n)}
                      {maturityCell(s, n)}
                    </React.Fragment>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt1(s.avg_strength)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt1(s.avg_ea)}</TableCell>
                  {!readOnly && (
                    <>
                      <TableCell padding="checkbox" align="center">
                        <Tooltip title={s.is_exclude ? 'Use this result in the curve' : 'Leave this result out of the curve'}>
                          <Checkbox size="small" checked={!s.is_exclude}
                            onChange={(e) => toggle(s, 'is_exclude', !e.target.checked)} inputProps={{ 'aria-label': 'use in curve' }} />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                        <IconButton size="small" onClick={() => startEdit(s)} aria-label="edit test result"><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => remove(s)} aria-label="delete test result"><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ),
            )}
            {adding && editRow('new')}
            {samples.length === 0 && !adding && (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <Typography variant="body2" color="text.secondary">No test results yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!readOnly && !adding && !editingId && (
        <Box sx={{ mt: 1.5 }}>
          <Tooltip title={isSystem && specimens.length === 0 ? 'Choose the pour and loggers first' : ''}>
            <span>
              <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={startAdd}
                disabled={isSystem && specimens.length === 0}>
                Add new
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default CalibrationResultsGrid;
